import UIController, {uiSetTotalLoss} from "../UIController";
import {inject} from "inversify";
import SERVICE_IDENTIFIER from "../../constants/identifiers";
import SystemConfig, {CONFIG_PARAMS, MODES} from "../../system/SystemConfig";
import IAudio from "../../engine/IAudio";
import System from "../../system/System";
import {AUDIO_EVENTS} from "../../engine/AbstractAudio";
import DesktopDOM, {ELEMENT_IDS} from "./DesktopDOM";
import {ACTION_EVENTS} from "../UserAction";
import events from "../../events/EventsObservable";
import {EVENTS} from "../../events/baseEvents";
import {getTime, numberFormat, replaceParam} from "../../helpers";
import {BASE_ELEMENT_IDS as BASE_ELEMENT_IDS} from "../BaseDOM";
import Wallet, {WALLET_EVENTS} from "../../wallet/Wallet";
import {AUTOSTART_EVENTS, MAX_AUTOSTART} from "../../game/GameAutostart";
import MadnesIframe from "../components/MadnesIframe";
import {FULLSCREEN_EVENTS} from "../screen/Fullscreen";
import IGame from "../../game/IGame";
import IStatesManager from "../../states/IStatesManager";
import {STATES} from "../../states/states";
import {GAME_EVENTS} from "../../game/gameEvents";
import {MESSAGE_EVENTS} from "../../error/errorMessages";
import {translate} from "../../translations/translations";
import { withCurrency } from "../../wallet/walletOperations";
import MaxBetWarning from "../components/MaxBetWarning";
import IUIActions from "../IUIActions";

export default class DesktopUIController extends UIController {

    protected _wallet: Wallet;
    protected dom: DesktopDOM;

    protected isBigWin: boolean = false;
    protected disableAutostart: boolean;

    protected maxBetWarning: MaxBetWarning;

    @inject(SERVICE_IDENTIFIER.UIActions) protected uiActions: IUIActions;

    constructor(
        @inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
        @inject(SERVICE_IDENTIFIER.DOM) dom: DesktopDOM,
        @inject(SERVICE_IDENTIFIER.StatesManager) statesManager: IStatesManager,
        @inject(SERVICE_IDENTIFIER.Game) game: IGame,
        @inject(SERVICE_IDENTIFIER.EngineAudio) audio: IAudio,
        @inject(SERVICE_IDENTIFIER.System) system: System,
        @inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet
    ) {

        super(config, dom, statesManager, game, audio, system);

        this._wallet = wallet;
        this.maxBetWarning = new MaxBetWarning(
            config,
            dom.uiMessages,
            (blocked) => {
                this.uiActions.blockActions(
                    [
                        ACTION_EVENTS.ACTION_SPIN,
                        ACTION_EVENTS.ACTION_AUTOSTART_OPEN,
                        ACTION_EVENTS.ACTION_AUTOSTART_CHOOSE,
                        ACTION_EVENTS.ACTION_AUTOSTART_CONTINUE,
                    ],
                    blocked
                );
                this.states.reset();
            },
            () => this.getSpinButtonAnchor()
        );
    }

    subscribe(): void {
        super.subscribe()

        events.subscribe(WALLET_EVENTS.WALLET_ON_INITED, (eventName, data) => this.walletOnInited())
        events.subscribe(
            [
                WALLET_EVENTS.WALLET_ON_SET_BALANCE,
                WALLET_EVENTS.WALLET_ON_SET_FREESPINS
            ],
            (eventName, data) => this.walletSetBalance(data)
        )
        events.subscribe(
            [
                WALLET_EVENTS.WALLET_ON_SET_COIN,
                WALLET_EVENTS.WALLET_ON_SET_BET,
                WALLET_EVENTS.WALLET_ON_SET_LINES_COUNT
            ],
            (eventName, data) => this.walletOnSetBet(data)
        )

        events.subscribe(WALLET_EVENTS.WALLET_ON_TOTAL_LOSS, (eventName, data) => this._walletSetTotalLoss(data))
        events.subscribe(WALLET_EVENTS.WALLET_ON_SET_WIN, (eventName, data) => this.walletSetWin(data))
        events.subscribe(AUDIO_EVENTS.AUDIO_MUTED, (eventName, data) => this.audioMuted(data))

        events.subscribe(ACTION_EVENTS.ACTION_AUTOSTART_OPEN, () => this._autostartMenuOpen())
        events.subscribe(ACTION_EVENTS.ACTION_AUTOSTART_CLOSE, () => this._autostartMenuClose())
        events.subscribe(ACTION_EVENTS.ACTION_AUTOSTART_CHOOSE, (eventName, data) => this._autostartChoose(data))
        events.subscribe(ACTION_EVENTS.ACTION_AUTOSTART_CONTINUE , () => this.autostartContinue())
        events.subscribe(AUTOSTART_EVENTS.AUTOSTART_ON_SET_COUNT, (eventName, data) => this._autostartUpdateEvent(data))

        events.subscribe(ACTION_EVENTS.ACTION_MAXBET, () => this._maxbet())
        events.subscribe(ACTION_EVENTS.ACTION_COIN_DEC, () => this.coinDec())
        events.subscribe(ACTION_EVENTS.ACTION_COIN_INC, () => this.coinInc())
        events.subscribe(ACTION_EVENTS.ACTION_BET_DEC, () => this.betDec())
        events.subscribe(ACTION_EVENTS.ACTION_BET_INC, () => this.betInc())
        events.subscribe(ACTION_EVENTS.ACTION_LINES_DEC, () => this.linesDec())
        events.subscribe(ACTION_EVENTS.ACTION_LINES_INC, () => this.linesInc())

        events.subscribe(EVENTS.INFO_ON_OPEN, () => this._infoOpen())
        events.subscribe(EVENTS.INFO_ON_CLOSE, () => this._infoClose())

        events.subscribe(FULLSCREEN_EVENTS.FULLSCREEN_CHANGE, (eventName, data) => this.fullscreenChange(data))
        events.subscribe(EVENTS.BEFORE_SPIN,() => this.beforeSpin())
        events.subscribe(GAME_EVENTS.GAME_BIGWIN, () => this.onBigWin())
        events.subscribe(GAME_EVENTS.GAME_END_ROUND_RECALCED, () => this.onEndRound())
        events.subscribe([MESSAGE_EVENTS.SWITCH_TO_FREESPINS, MESSAGE_EVENTS.SWITCH_TO_REAL], () => this.switchToFreespins())
        // IDLE only: the launch dialog can fire this during PREINFO / SOUND
        events.subscribe(GAME_EVENTS.GAME_FS_MODE_CHANGED, async () => {
            if (this.states.getState() === STATES.IDLE) await this.states.reset()
        })

        events.subscribe(ACTION_EVENTS.ACTION_IFRAME_CLOSE, (eventName, data) => {
            (data as MadnesIframe).close();
        })

        this.setDisableAutostart()
    }

    setDisableAutostart(): void {
        this.disableAutostart = this.config.get(CONFIG_PARAMS.LANGUAGE) === "nl";
    }

    walletOnInited(): void {
        this.dom.initBottomBar();
        this.dom.createAutostartMenu();
    }

    audioMuted(data): void {
        this.dom.toggleSoundButton(data);
    }

    autostartContinue(): void {
        this.game.autostart?.continue();
        if(this.game.autostart?.isAutostart()) {
			events.dispatch(GAME_EVENTS.GAME_AUTOSTART_START)
            events.dispatch(EVENTS.SPIN);
        }
    }

    coinDec(): void {
        this._wallet.decCoin();
    }

    coinInc(): void {
        this._wallet.incCoin();
    }

    linesDec(): void {
        this._wallet.decLinesCount();
    }

    linesInc(): void {
        this._wallet.incLinesCount();
    }

    betDec(): void {
        this._wallet.decCreditBet();
    }

    betInc(): void {
        this._wallet.incCreditBet();
    }

    async actionSpin(data): Promise<void> {
        super.actionSpin(data)
        this.dom.autostart?.hide();
        await events.dispatch(EVENTS.INFO_CLOSE_REQUEST);
    }

    fullscreenChange(data): void {
        this.dom.toggleFullscreenButton(data.inFullscreen);
    }

    async beforeSpin(): Promise<void> {
        if (this._wallet.getWin() > 0){
            await this.dom.winToBalance(false);
            await this.dom.fadeOutWin();
        }
    }

    switchToFreespins(): void {
        this._autostartMenuClose()
        if(this._wallet.isBonus() && this.config.get(CONFIG_PARAMS.USE_FS_TOTAL_WIN)){
            const _creditBalance = numberFormat(this._wallet.moneyToCredit(this._wallet.getFSTotalWin()), this._wallet.getCreditWinDecimalPlaces() || this._wallet.getDecimalPlaces(), ".", " ");
            // const _creditBalance = numberFormat(this._wallet.getFSTotalWin(), this._wallet.getCreditWinDecimalPlaces() || this._wallet.getDecimalPlaces(), ".", " ");
            const _realBalanceCurrency=withCurrency(Number(this._wallet.getRealFsTotalWin()), this._wallet.getCurrency(),this._wallet.getDecimalPlaces())
            $("#" + ELEMENT_IDS.CREDIT).text(_creditBalance);
            $("#" + ELEMENT_IDS.BALANCE).text(_realBalanceCurrency);
        }
    }

    protected walletSetBalance({creditBalance, realBalanceCurrency, freespins}) {
        if (this.config.get(CONFIG_PARAMS.MODE) !== MODES.REPLAY) {
			let _realBalanceCurrency = realBalanceCurrency
            let _creditBalance = numberFormat(creditBalance, 0, ".", " ");

			if(!(this._wallet.isBonus() && this.config.get(CONFIG_PARAMS.USE_FS_TOTAL_WIN))){
                $("#" + ELEMENT_IDS.CREDIT).text(_creditBalance);
                $("#" + ELEMENT_IDS.BALANCE).text(_realBalanceCurrency);
			}
            $("#" + ELEMENT_IDS.FREE_SPINS).text(freespins);
            this.dom.refreshFsButtonLabel();
        } else {
            // Credit and Balance are set to * in replay mode
            $("#" + ELEMENT_IDS.CREDIT).text("****");
            $("#" + ELEMENT_IDS.BALANCE).text("****");
        }
    }

    protected walletSetWin({realWinCurrency, creditWin, win, atomicBet}) {

        creditWin > 0 && $("#" + ELEMENT_IDS.WIN_BOX).addClass("bar-bottom-box-win");
        let winRealElement = $("#" + ELEMENT_IDS.WIN_REAL);
        let winElement = $("#" + ELEMENT_IDS.WIN);

        winRealElement.text(realWinCurrency);
        winElement.text(numberFormat(creditWin, this._wallet.getCreditWinDecimalPlaces() || this._wallet.getDecimalPlaces(), '.', ' '));
        creditWin === 0 ? winRealElement.hide() : winRealElement.show();
        creditWin === 0 ? winElement.hide() : winElement.show();


        // if not in bigwin and win higher than bet, highlight win
        if (win > atomicBet && !this.isBigWin) {
            this.dom.highlightWin();
        }
    }

    private walletSetBet({creditBetLines, creditBetAtomic, realBetCurrency, coinValue, linesCount}) {
        $("#" + ELEMENT_IDS.BET_LINE_TOTAL).text(creditBetLines);
        $("#" + ELEMENT_IDS.BET_CREDIT_ATOMIC).text(numberFormat(creditBetAtomic, 0, ".", " "));

        $("#" + ELEMENT_IDS.BET_REAL).text(realBetCurrency);

        $("#" + ELEMENT_IDS.COIN_VALUE).text(coinValue);
        $("#" + ELEMENT_IDS.LINES).text(linesCount);
    }

    protected timeUpdate({h, m, s}) {
        let time = getTime({h, m, s});

        let element = document.getElementById(BASE_ELEMENT_IDS.TIME);
        if (element) {
            if (element.textContent) {
                element.textContent = `${time}`;
            } else {
                element.innerText = `${time}`;
            }
        }
    }

    private _walletSetTotalLoss({loss, lossWithCurrency}) {
        if(super._isValidClientPlatform() && this.config.get(CONFIG_PARAMS.LEGISLATION) === "cz") {
            uiSetTotalLoss({loss, lossWithCurrency});
        }
    }

    protected _autostartUpdateEvent({count}) {
        let buttons = $("#" + BASE_ELEMENT_IDS.BUTTON_STOP_AUTOSTART);
        buttons.text(count > MAX_AUTOSTART || count < 1 ? "" : count);
    }

    _autostartMenuOpen() {
        this.dom.autostart?.open();
    }

    async _autostartMenuClose() {

        this.dom.autostart?.hide();

        await this.states.reset();

        if(this.game.autostart?.isAutostart()) {
			events.dispatch(GAME_EVENTS.GAME_AUTOSTART_START)
            events.dispatch(EVENTS.SPIN);
        }
    }

    protected async _autostartChoose(data) {
        await events.dispatch(EVENTS.INFO_CLOSE_REQUEST);
        this.game.autostart?.setAutostart(data);
        this._autostartMenuClose();
    }

    protected getSpinButtonAnchor(): { x: number; y: number } | undefined {
        for (const button of Object.values(this.dom.spinBtns)) {
            const rect = button?.getBoundingClientRect();
            if (!rect?.width || !rect.height) continue;
            return { x: rect.left + rect.width / 2, y: rect.top };
        }
        return undefined;
    }

    protected _maxbet() {
        this._wallet.setMaxBet()
        setTimeout(() => this.maxBetWarning.show())
    }

    protected _infoOpen() {
        let wintableControlsWrapper = document.getElementById(BASE_ELEMENT_IDS.WINTABLE_CONTROLS_WRAPPER);
        $(wintableControlsWrapper).show();
    }

    protected _infoClose() {
        let wintableControlsWrapper = document.getElementById(BASE_ELEMENT_IDS.WINTABLE_CONTROLS_WRAPPER);
        $(wintableControlsWrapper).hide();
    }

    protected walletOnSetBet(data) {
        this.maxBetWarning.hideToast();
        this.states.reset();
        this.walletSetBet(data);
        this.walletSetBalance(data);
    }

    protected onBigWin() {
        this.isBigWin = true;
        $(document.getElementById(ELEMENT_IDS.WIN_BOX)).addClass('bar-bottom-box-big-win');
    }

    protected onEndRound() {
        this.isBigWin = false;
        if (!this.game.isInFreeSpins()) {
            this.dom.winToBalance().then(() => {
                this._wallet.addWinToBalance();
                this._wallet.updateBalanceUI();
            });
        }
        $(document.getElementById(ELEMENT_IDS.WIN_BOX)).removeClass('bar-bottom-box-big-win');
        if(this._wallet.isBonus() && this.config.get(CONFIG_PARAMS.USE_FS_TOTAL_WIN)){
            const _creditBalance = numberFormat(this._wallet.moneyToCredit(this._wallet.getFSTotalWin()), this._wallet.getCreditWinDecimalPlaces() || this._wallet.getDecimalPlaces(), ".", " ");
            // const _creditBalance = numberFormat(this._wallet.getFSTotalWin(), this._wallet.getCreditWinDecimalPlaces() || this._wallet.getDecimalPlaces(), ".", " ");
            const _realBalanceCurrency=withCurrency(Number(this._wallet.getRealFsTotalWin()), this._wallet.getCurrency(),this._wallet.getDecimalPlaces())
            $("#" + ELEMENT_IDS.CREDIT).text(_creditBalance);
            $("#" + ELEMENT_IDS.BALANCE).text(_realBalanceCurrency);
        }
    }
}
